import qs from 'query-string';

import { getValidQuery } from '../../Search/helpers/query';

import { ThrowError } from './errors';

export type HashLocation = {
  pathname: string;
  search: string;
};

export const getHashLocation = (hash: string = window.location.hash): HashLocation => {
  const url = new URL(hash.split('#').pop()!, 'https://anyclip.com/');

  return {
    pathname: url.pathname,
    search: url.search.split('?').pop() ?? '',
  };
};

export type HistoryObject = {
  pathname?: string;
  search?: string | Record<string, string>;
};

export const processHistoryObject = ({
  pathname = getHashLocation().pathname,
  search: search$,
}: HistoryObject): string => {
  const search = search$ || '';
  const type = Object.prototype.toString.call(search);

  if (!['[object String]', '[object Object]'].includes(type)) {
    ThrowError(`search should be type query-string or object query-string => ${type}`);
  }

  const searchQuery = qs.stringify(
    type === '[object String]' ? getValidQuery(qs.parse(search as string)) : (search as Record<string, string>),
  );

  return pathname + (searchQuery ? `?${searchQuery}` : '');
};

type NavigationHistory = {
  target: string | null;
  prev: string | null;
};

const navigate: NavigationHistory = {
  target: null,
  prev: null,
};

export const getPrevPath = (): string | null => navigate.prev;
export const getTargetPath = (): string | null => navigate.target;

export const destroyHistoryRoutePath = (): void => {
  navigate.prev = null;
  navigate.target = null;
};

export const updateHistoryRoutePath = (pathName: string | null): void => {
  if (pathName !== navigate.prev) {
    navigate.prev = navigate.target;
    navigate.target = pathName;
  }
};

type RawPath = string | HistoryObject;

const addHashHistory = (rawPath: RawPath, replace: boolean = false): void => {
  const newURL = typeof rawPath === 'string' ? rawPath.replace(/^#/, '') : processHistoryObject(rawPath);

  const { pathname, search } = window.location;
  window.history[replace ? 'replaceState' : 'pushState']({}, '', `${pathname}${search}#${newURL}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

export const push = (url: RawPath): void => addHashHistory(url);

export const replace = (url: RawPath): void => addHashHistory(url, true);

export const getPathNameByParams = (path: string, params: Record<string, string> = {}): string =>
  path
    .split('/')
    .map((item) => {
      if (/^:/.test(item)) {
        const prop = item.replace(':', '');

        if (!Object.prototype.hasOwnProperty.call(params, prop)) {
          ThrowError(`Property name ${prop} mismatch in the ${path}`);
        }

        return params[prop];
      }

      return item;
    })
    .join('/');
